// MarkingMenu — радиальное меню по правому клику, как в Fusion 360.
//
// Зачем именно радиальное, а не список: команда выбирается НАПРАВЛЕНИЕМ
// движения мыши, а не чтением строк. Рука быстро запоминает жест, и через
// несколько дней работы выбор команды перестаёт требовать взгляда — это
// главная причина, почему в Fusion работают быстро.
//
// Раскладка повторяет Fusion:
//   вверху  — «Повторить» (последняя команда),
//   слева   — «Отменить», справа — «Вернуть»,
//   остальные секторы — команды под текущий выбор (тело / профиль / пусто).
//
// Жест: если зажать правую кнопку и дёрнуть в сторону дальше порога,
// команда выполняется сразу, без показа меню.
import React, { useEffect, useRef, useState, useCallback } from 'react';

const R_INNER = 34;     // «мёртвая» зона в центре — отпускание здесь ничего не делает
const R_OUTER = 104;    // радиус кольца с подписями
const GESTURE_PX = 26;  // после такого сдвига жест засчитывается без меню

/**
 * @param {{x:number,y:number}} at      — где открыть (координаты экрана)
 * @param {Array} items                 — 8 секторов, по часовой с ВЕРХНЕГО:
 *                                        [верх, вправо-вверх, вправо, ...]
 *                                        элемент: {id,label,icon,disabled} | null
 * @param {(id:string)=>void} onPick
 * @param {()=>void} onClose
 */
export default function MarkingMenu({ at, items, onPick, onClose }) {
    const [hover, setHover] = useState(-1);
    const rootRef = useRef(null);

    // Сектор под курсором: 8 направлений, отсчёт от верхнего по часовой.
    const sectorAt = useCallback((cx, cy) => {
        const dx = cx - at.x;
        const dy = cy - at.y;
        const d = Math.hypot(dx, dy);
        if (d < R_INNER) return -1;
        // atan2 даёт угол от оси X против часовой; переводим так, чтобы
        // 0 был вверху и рос по часовой.
        let a = Math.atan2(dx, -dy) * 180 / Math.PI;
        if (a < 0) a += 360;
        return Math.round(a / 45) % 8;
    }, [at]);

    useEffect(() => {
        const onMove = (e) => setHover(sectorAt(e.clientX, e.clientY));
        const onUp = (e) => {
            const s = sectorAt(e.clientX, e.clientY);
            const it = s >= 0 ? items[s] : null;
            if (it && !it.disabled) onPick(it.id);
            onClose();
        };
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        // Правый клик поверх меню не должен открывать системное меню.
        const onCtx = (e) => { e.preventDefault(); };
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
        window.addEventListener('keydown', onKey);
        window.addEventListener('contextmenu', onCtx);
        return () => {
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
            window.removeEventListener('keydown', onKey);
            window.removeEventListener('contextmenu', onCtx);
        };
    }, [sectorAt, items, onPick, onClose]);

    // Позиция подписи сектора: на кольце, по центру своего направления.
    const labelPos = (i) => {
        const a = (i * 45 - 90) * Math.PI / 180;   // -90°, чтобы 0 был вверху
        const r = (R_INNER + R_OUTER) / 2 + 8;
        return { left: at.x + Math.cos(a) * r, top: at.y + Math.sin(a) * r };
    };

    return (
        <div
            ref={rootRef}
            style={{
                position: 'fixed', inset: 0, zIndex: 4000,
                // Меню ловит движение мыши, но не перехватывает клики мимо:
                // отпускание обрабатываем на window (см. выше).
                pointerEvents: 'none',
            }}
        >
            {/* Кольцо-подложка */}
            <svg
                width={R_OUTER * 2 + 20} height={R_OUTER * 2 + 20}
                style={{
                    position: 'fixed',
                    left: at.x - R_OUTER - 10, top: at.y - R_OUTER - 10,
                    filter: 'drop-shadow(0 6px 20px rgba(0,0,0,.45))',
                }}
            >
                {items.map((it, i) => {
                    if (!it) return null;
                    const a0 = (i * 45 - 90 - 22.5) * Math.PI / 180;
                    const a1 = (i * 45 - 90 + 22.5) * Math.PI / 180;
                    const cx = R_OUTER + 10, cy = R_OUTER + 10;
                    const p = (r, a) => `${cx + Math.cos(a) * r} ${cy + Math.sin(a) * r}`;
                    const d = [
                        `M ${p(R_INNER, a0)}`,
                        `L ${p(R_OUTER, a0)}`,
                        `A ${R_OUTER} ${R_OUTER} 0 0 1 ${p(R_OUTER, a1)}`,
                        `L ${p(R_INNER, a1)}`,
                        `A ${R_INNER} ${R_INNER} 0 0 0 ${p(R_INNER, a0)}`,
                        'Z',
                    ].join(' ');
                    const active = hover === i && !it.disabled;
                    return (
                        <path
                            key={it.id}
                            d={d}
                            fill={active ? 'rgba(0,176,240,.85)' : 'rgba(38,42,50,.94)'}
                            stroke="rgba(255,255,255,.10)"
                            strokeWidth="1"
                        />
                    );
                })}
            </svg>

            {/* Подписи секторов */}
            {items.map((it, i) => {
                if (!it) return null;
                const pos = labelPos(i);
                const active = hover === i && !it.disabled;
                return (
                    <div
                        key={it.id}
                        style={{
                            position: 'fixed',
                            left: pos.left, top: pos.top,
                            transform: 'translate(-50%, -50%)',
                            font: '12px/1.2 system-ui, sans-serif',
                            fontWeight: active ? 700 : 500,
                            color: it.disabled ? 'rgba(255,255,255,.35)'
                                : active ? '#0b1a22' : '#e6edf3',
                            textAlign: 'center', width: 84,
                            pointerEvents: 'none', whiteSpace: 'nowrap',
                            overflow: 'hidden', textOverflow: 'ellipsis',
                        }}
                    >
                        {it.label}
                    </div>
                );
            })}
        </div>
    );
}

export { GESTURE_PX };
